import { useCallback, useEffect, useRef } from "react";

/**
 * Lang indrukken herkennen, zonder library.
 *
 * Alleen voor touch en pen: met een muis zijn er ctrl- en shift-klik, en een
 * muisknop die per ongeluk een halve seconde blijft hangen hoort geen
 * keuzemodus te starten.
 *
 * Scrollen mag nooit per ongeluk een selectie opleveren. Daar zijn twee
 * vangnetten voor: de browser stuurt `pointercancel` zodra een aanraking in een
 * scrollgebaar verandert, en we breken zelf af bij meer dan een paar pixels
 * beweging — Android stuurt die annulering niet altijd meteen.
 */

const DELAY_MS = 500;
const SLOP_PX = 10;

export interface LongPressHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}

export interface LongPress {
  handlers: (uid: number) => LongPressHandlers;
  /**
   * True zolang de klik die ná een geslaagde long-press komt genegeerd moet
   * worden. De rij vraagt dit op in zijn onClick en zet hem daarmee terug.
   */
  consumeClick: () => boolean;
}

export function useLongPress(onFire: (uid: number) => void, enabled: boolean): LongPress {
  const timer = useRef<number | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const swallow = useRef(false);

  const cancel = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    start.current = null;
  }, []);

  useEffect(() => cancel, [cancel]);

  const handlers = useCallback(
    (uid: number): LongPressHandlers => ({
      onPointerDown: (e) => {
        if (!enabled || e.pointerType === "mouse") return;
        start.current = { x: e.clientX, y: e.clientY };
        timer.current = window.setTimeout(() => {
          timer.current = null;
          swallow.current = true;
          // Een korte tik bevestigt dat de keuzemodus aanstaat; niet elk
          // toestel kent vibrate, vandaar de optionele aanroep.
          navigator.vibrate?.(10);
          onFire(uid);
        }, DELAY_MS);
      },
      onPointerMove: (e) => {
        const from = start.current;
        if (from === null) return;
        if (Math.abs(e.clientX - from.x) > SLOP_PX || Math.abs(e.clientY - from.y) > SLOP_PX) {
          cancel();
        }
      },
      onPointerUp: cancel,
      onPointerCancel: cancel,
      onContextMenu: (e) => {
        // Zonder dit legt Android het eigen tekstselectiemenu over de onze heen.
        if (enabled) e.preventDefault();
      },
    }),
    [cancel, enabled, onFire],
  );

  const consumeClick = useCallback(() => {
    if (!swallow.current) return false;
    swallow.current = false;
    return true;
  }, []);

  return { handlers, consumeClick };
}
